// The single way to run the Schedule Builder. Two entry points share it — the
// agenda block's buttons and the /build-schedule deep link (app/middleware/
// modal.global.js -> app/plugins/modal-route.client.js) — and both must count the
// interaction and settle the session identically, so that logic lives here rather
// than being duplicated at each call site.
//
// Imported directly (not via resolveComponent) for the same reason the modal-route
// plugin does: resolveComponent only works inside a component's setup()/render().
import ScheduleBuilderModal from '~/components/fragments/dialogs/schedule-builder.vue';

export const useScheduleBuilderLauncher = () => {
    const { open } = useModal();
    const { $bus } = useNuxtApp();
    const { fetch: refreshSession } = useUserSession();
    const builder = useScheduleBuilder();

    const launchScheduleBuilder = async () => {
        builder.recordInteraction();
        // Reset to 'prefs' before every open: reopening after a prior build would
        // otherwise jump straight to the 'complete'/'full' view instead of starting
        // fresh.
        builder.state.value = 'prefs';

        await open(ScheduleBuilderModal, {}); // resolves when the modal closes

        if (builder.state.value !== 'complete' && builder.state.value !== 'full') return;

        // The SSE build couldn't write the cookie mid-stream, so re-hydrate
        // scheduled/scheduledGenerated from D1 before refreshSession reads it.
        await $fetch('/api/v1/schedule/hydrate', { method: 'POST' });
        await refreshSession();

        // Land on the schedule that was just built (UNB-618). Previously the user
        // stayed wherever they were — for Steph, the All Sessions tab, which
        // shows no sign of the work that just happened. Done here rather than in
        // the agenda block so the nav's /build-schedule deep link, which can fire
        // from any page, ends up in the same place.
        //
        // Deliberately an ALLOWLIST rather than spreading the current query.
        // Every other agenda param is a filter or a scroll marker — day, role,
        // themes, time_range and the rest — and carrying one over would show a
        // freshly built schedule through a narrow filter, which reads as an empty
        // result. It also keeps a stale `modal=/build-schedule` from riding along
        // and immediately reopening the builder we just closed.
        await navigateTo({
            path: '/agenda',
            query: { tab: 'myschedule', view: 'overview' },
        });

        // Listened for by the agenda block, which refreshes its feed. Emitted rather
        // than called directly because the builder can now be launched from the nav
        // on any page, with no agenda instance to talk to.
        $bus.emit('schedule:built');
    };

    return { launchScheduleBuilder };
};
